// Loan-context Ask Addy suggestions for the sidebar empty state.
// Mirrors the structure used by the loan-page AskAddyAI card so the
// visible question stays simple while the hidden prompt sent to the
// model carries the full instruction set.

const LOAN_OFFICER_SUGGESTIONS = [
  {
    id: "credit",
    label: "Credit",
    svgIconPath: "/suggested_ask_question.svg",
    prompts: [
      {
        label: "What are the biggest credit issues in this file?",
        prompt:
          "Review the active loan file and identify the top credit-related issues that could affect qualification, AUS results, approval strength, or loan salability. Use the credit report, liabilities, credit events, tradelines, housing history, and checklist findings. Rank the issues by importance, explain why each matters, and summarize what needs to happen next.",
      },
      {
        label: "How is credit affecting this borrower's approval?",
        prompt:
          "Explain how the borrower's credit profile is impacting the strength of the loan file. Review score, liabilities, credit events, payment history, tradelines, and any related findings. Summarize whether credit is helping, hurting, or limiting the file and explain the biggest reasons why in plain English.",
      },
      {
        label: "What liabilities are hurting DTI the most?",
        prompt:
          "Review the credit report, liabilities, and DTI-related findings. Identify the liabilities with the greatest impact on the borrower's debt-to-income ratio. Break down each liability, the monthly payment used, and whether any item appears to need clarification, correction, payoff, or documentation.",
      },
      {
        label: "Are there any credit items that need explanation?",
        prompt:
          "Review the active loan file and identify any credit-related items that may require explanation, supporting documentation, or follow-up. Focus on late payments, disputed accounts, collections, charge-offs, inquiries, credit events, housing history issues, or other items that may raise underwriting questions. Summarize what may need to be addressed and why.",
      },
      {
        label: "What should I be talking to the borrower about on credit?",
        prompt:
          "Review the borrower's credit profile and identify the most important credit-related topics the loan officer should discuss with the borrower. Focus on items affecting qualification, DTI, documentation, or approval strength. Summarize the conversation points in a practical, borrower-friendly way and include the next best actions.",
      },
    ],
  },
  {
    id: "income",
    label: "Income",
    svgIconPath: "/suggested_calculate_income.svg",
    prompts: [
      {
        label: "Compare my income to Addy's income calculation",
        prompt:
          "Compare Addy's qualifying income calculation to the income declared on the 1003, MISMO, or loan application. Break down included and excluded income sources, identify supporting documents used, explain all differences, and summarize whether the variance is due to documentation, averaging, calculation approach, or guideline treatment.",
      },
      {
        label: "Why didn't Addy use all of this income?",
        prompt:
          "Review all income sources in the active loan file and explain why any income was excluded, reduced, or treated differently in Addy's qualifying income calculation. Separate guideline-based exclusions from documentation gaps or file inconsistencies. Clearly explain what was not used, why, and what may be needed for reconsideration.",
      },
      {
        label: "What is the biggest income issue in this file?",
        prompt:
          "Identify the single biggest income-related issue affecting this file. Review the income documents, employment profile, Addy's qualifying income results, and checklist findings. Explain why this issue matters, what evidence supports it, and what should happen next to strengthen the file.",
      },
      {
        label: "What income is still missing documentation?",
        prompt:
          "Review the declared income, available documents, and Addy's income analysis. Identify all income sources that are incomplete, insufficiently documented, or still need clarification before they can be fully supported for qualification. Explain what is missing, why it matters, and the likely impact on the file.",
      },
      {
        label: "What would help this borrower qualify stronger on income?",
        prompt:
          "Based on the current file, identify legitimate ways the borrower's income profile could become stronger from a qualification standpoint. Focus on additional documentation, clarified employment history, complete income support, or other guideline-consistent improvements. Do not speculate beyond the file. Summarize the highest-impact opportunities first.",
      },
    ],
  },
  {
    id: "assets",
    label: "Assets",
    svgIconPath: "/suggested_pipeline_review.svg",
    prompts: [
      {
        label: "Do they have enough money to close?",
        prompt:
          "Review the borrower's available asset documentation and explain the current funds-to-close position. Identify counted funds, excluded funds, any sourcing or seasoning concerns, reserve considerations, and whether the borrower appears sufficient, short, or uncertain based on the current file.",
      },
      {
        label: "What assets are Addy counting?",
        prompt:
          "Review the borrower's asset documentation and summarize which funds Addy is counting toward funds to close and reserves. For each account or asset source, explain what is being counted, what is not being counted, and why.",
      },
      {
        label: "Why aren't all of these funds being used?",
        prompt:
          "Review all documented assets in the active loan file and explain why certain funds are not being counted toward funds to close or reserves. Separate documentation issues, sourcing issues, seasoning concerns, and guideline-based limitations. Clearly summarize each excluded or limited asset and the reason.",
      },
      {
        label: "Are there any large deposits I should worry about?",
        prompt:
          "Review the borrower's asset statements and identify any deposits that appear large, unusual, or likely to require review or sourcing. Explain which deposits matter most, why they matter, and what documentation or follow-up may be needed.",
      },
      {
        label: "What asset issues could slow this file down?",
        prompt:
          "Review the borrower's assets, funds-to-close analysis, and checklist findings. Identify the most important asset-related issues that could delay processing, underwriting, or approval. Explain each issue, why it matters, and what should happen next to keep the file moving.",
      },
    ],
  },
];

const PROCESSOR_SUGGESTIONS = [
  {
    id: "credit",
    label: "Credit",
    svgIconPath: "/suggested_ask_question.svg",
    prompts: [
      {
        label: "What credit items still need to be cleaned up?",
        prompt:
          "Review the active loan file and identify all unresolved credit-related issues that may require cleanup before underwriting or approval. Focus on documentation gaps, mismatches, liabilities needing clarification, credit events, inquiries, and explanation items. Organize the answer into a practical cleanup list.",
      },
      {
        label: "What needs documentation or explanation on credit?",
        prompt:
          "Review the credit report, checklist findings, and active loan file to identify any credit items that may require documentation, letters of explanation, or additional follow-up. Summarize each item, explain why it may matter, and recommend what support should be collected.",
      },
      {
        label: "Are there any liability issues I should review?",
        prompt:
          "Review the borrower's liabilities and identify any issues that may need processor attention. Focus on inaccurate payments, duplicate liabilities, disputed accounts, accounts to be paid off, omitted obligations, or inconsistencies between the application and the credit report. Summarize the items that may need action.",
      },
      {
        label: "What is the biggest credit risk for underwriting?",
        prompt:
          "Identify the single biggest credit-related risk that may draw underwriting attention in this file. Explain why it matters, what evidence supports it, and what documentation or prep work could reduce friction before the file goes to underwriting.",
      },
      {
        label: "Turn the credit review into processor next steps",
        prompt:
          "Review the full credit analysis and convert the findings into a processor-style action list. Organize the response into clear next steps, including what to collect, what to verify, what to clarify, and what to monitor before the file moves forward.",
      },
    ],
  },
  {
    id: "income",
    label: "Income",
    svgIconPath: "/suggested_calculate_income.svg",
    prompts: [
      {
        label: "What income documentation is still missing?",
        prompt:
          "Review the declared income, available income documents, and Addy's income analysis. Identify all missing, incomplete, or unclear income documentation that is preventing a clean income review. Summarize the missing items, why they are needed, and which ones matter most.",
      },
      {
        label: "What part of the income needs clarification?",
        prompt:
          "Review the income file and identify any areas where the income analysis may need clarification, additional support, or follow-up. Focus on employment gaps, fluctuating earnings, overtime, bonus, commission, self-employment documentation, inconsistent pay, or unclear trends. Explain what needs clarification and why.",
      },
      {
        label: "Why is Addy calculating the income this way?",
        prompt:
          "Explain Addy's qualifying income calculation in a processor-friendly way. Break down each income source, the documents used, what was included, what was excluded, and any averaging or treatment applied. Highlight the parts that may create conditions or processor follow-up.",
      },
      {
        label: "What should I collect to support this income?",
        prompt:
          "Review the active loan file and identify the documents, explanations, or supporting items that would best strengthen the current income analysis. Prioritize the items that would reduce underwriting friction or resolve uncertainty in the file.",
      },
      {
        label: "Turn the income review into processor next steps",
        prompt:
          "Review the full income analysis and convert the findings into a processor-style next-step checklist. Organize the response into what needs to be collected, clarified, verified, or reviewed before underwriting. Prioritize by impact and urgency.",
      },
    ],
  },
  {
    id: "assets",
    label: "Assets",
    svgIconPath: "/suggested_pipeline_review.svg",
    prompts: [
      {
        label: "What asset documentation is still missing?",
        prompt:
          "Review the asset file, funds-to-close analysis, and checklist findings. Identify all missing, incomplete, or unclear asset documents needed to support funds to close, reserves, sourcing, seasoning, or transfer of funds. Summarize the missing items and why they matter.",
      },
      {
        label: "What funds are not fully supported yet?",
        prompt:
          "Review all asset accounts and identify any funds that are not fully supported for use in the loan. Separate issues related to missing statements, incomplete pages, sourcing concerns, seasoning concerns, unclear ownership, or transfer documentation. Explain each issue clearly.",
      },
      {
        label: "Do any deposits need to be sourced?",
        prompt:
          "Review the borrower's asset statements and identify deposits that may require sourcing or follow-up. Explain which deposits stand out, why they may matter, and what documentation or explanation should be collected to support them.",
      },
      {
        label: "What asset issues could create conditions?",
        prompt:
          "Review the asset analysis and identify the issues most likely to create underwriting conditions or processing friction. Focus on reserves, large deposits, gift funds, missing statements, transfer of funds, sourcing, seasoning, and funds-to-close uncertainty. Summarize the highest-risk issues first.",
      },
      {
        label: "Turn the asset review into processor next steps",
        prompt:
          "Review the asset analysis and convert the findings into a processor-style action list. Organize the answer into clear next steps showing what to collect, verify, explain, or monitor before the file goes to underwriting.",
      },
    ],
  },
];

/**
 * Returns dropdown buttons for the loan-page Ask Addy sidebar based on
 * organization member role. Returns an empty array when the role is
 * unsupported or when not in a loan context.
 *
 * Treats Admin as Loan Officer to match the loan-card behavior.
 */
export function getLoanRoleSuggestions({ memberRole, loanId }) {
  if (!loanId) return [];
  if (memberRole === "Loan Officer" || memberRole === "Admin") {
    return LOAN_OFFICER_SUGGESTIONS;
  }
  if (memberRole === "Processor") {
    return PROCESSOR_SUGGESTIONS;
  }
  return [];
}
